import React from 'react';
import { Award, Heart, Target } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { drRupesh, drRiya, drGopika, clinicInfo } from '../data/mockData';

const AboutPage = () => {
  const handleWhatsApp = () => {
    window.open(
      `https://wa.me/${clinicInfo.whatsapp.replace(/[^0-9]/g, '')}?text=Hi, I would like to book an appointment`,
      '_blank'
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-secondary to-background py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-primary mb-6">
                About Mind Motion
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed mb-6">
                Founded with a mission to make evidence-based, personalized physiotherapy accessible to people in and around Bellandur, Mind Motion believes pain is not something you should have to live with.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                We take a different approach. Instead of quick fixes, we focus on understanding you — your body, routine, and goals.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Because real recovery isn't just relief — it's moving better, feeling stronger, and living fully again.
              </p>
            </div>

            <div className="relative">
              <img
                src="/images/wmm1.jpg"
                alt="Mind Motion Clinic"
                className="rounded-lg shadow-xl w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Section Heading */}
      <div className="text-center mb-16">
        <h2 className="text-3xl md:text-5xl font-bold text-primary">
          Meet the Specialists Behind Your Recovery
        </h2>
      </div>

      {/* About Dr. Rupesh */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="grid md:grid-cols-2 gap-12 items-start">

            {/* TEXT */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
                About Dr. Rupesh
              </h2>

              <p className="text-lg text-muted-foreground mb-4">
                <strong>
                  Director & Lead Physiotherapist, Mind Motion Physiotherapy & Rehab Center
                </strong>
              </p>

              <p className="italic text-muted-foreground mb-6">
                Recovery isn't just about healing the body — it's about reclaiming your life.
              </p>

              <p className="text-muted-foreground mb-6 leading-relaxed">
                I'm Rupesh, a physiotherapist with a Master's degree in Neuro (Adult) Physiotherapy and over four years of experience.
              </p>

              <h3 className="text-xl font-bold text-primary mb-2">
                Neurological Rehabilitation
              </h3>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                I work with patients living with stroke, Parkinson's disease, traumatic brain injury, and nerve-related disorders.
              </p>

              <h3 className="text-xl font-bold text-primary mb-2">
                Musculoskeletal Care
              </h3>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Chronic pain and joint issues require a root-cause approach focused on long-term recovery.
              </p>

              <h3 className="text-xl font-bold text-primary mb-2">
                Mind Motion Physiotherapy & Rehab Center
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                Delivering personalized, evidence-based physiotherapy where every patient is treated individually.
              </p>
            </div>

            {/* IMAGE */}
            <div className="relative">
              <img
                src={drRupesh.image}
                alt="Dr. Rupesh Kumar"
                className="rounded-lg shadow-xl w-full h-[500px] object-cover"
              />
            </div>

          </div>

          <div className="mt-12 text-center">
            <p className="text-lg font-semibold text-primary">
              If you're ready to move better and live better, we're here to help.
            </p>
          </div>

        </div>
      </section>

      {/* About Dr. Riya */}
      <section className="py-16 md:py-24 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="grid md:grid-cols-2 gap-12 items-start">

            {/* TEXT */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
                About Dr. Riya Prasad
              </h2>

              <p className="text-lg text-muted-foreground mb-4">
                <strong>
                  Musculoskeletal Physiotherapist, Mind Motion Physiotherapy & Rehab Center
                </strong>
              </p>

              <p className="italic text-muted-foreground mb-6">
                The right rehabilitation does more than ease symptoms—it changes how you move, work, and live every day.
              </p>

              <p className="text-muted-foreground mb-6 leading-relaxed">
                I’m Riya Prasad, focused on evidence-based, patient-centered care to restore strength and confidence.
              </p>

              <h3 className="text-xl font-bold text-primary mb-2">
                Musculoskeletal Care
              </h3>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Treating spine and joint conditions including neck, back, shoulder, knee, and ankle issues.
              </p>

              <h3 className="text-xl font-bold text-primary mb-2">
                Purposeful Rehabilitation
              </h3>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Focused on mobility, strength, pain reduction, and long-term recovery.
              </p>

              <h3 className="text-xl font-bold text-primary mb-2">
                Hand & Upper Limb Focus
              </h3>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Specialized care for hand stiffness, weakness, and functional recovery.
              </p>

              <h3 className="text-xl font-bold text-primary mb-2">
                Individualized Approach
              </h3>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Combining clinical expertise with lifestyle and personal goals.
              </p>

              <h3 className="text-xl font-bold text-primary mb-2">
                Mind Motion Physiotherapy & Rehab Center
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                Providing thoughtful, high-quality rehabilitation with focused care.
              </p>
            </div>

            {/* IMAGE */}
            <div className="relative">
              <img
                src={drRiya.image}
                alt="Dr. Riya Prasad"
                className="rounded-lg shadow-xl w-full h-[700px] object-cover"
              />
            </div>

          </div>

        </div>
      </section>

      {/* About Dr. Gopika */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="grid md:grid-cols-2 gap-12 items-start">

            {/* TEXT */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
                About Dr. Gopika KP
              </h2>

              <p className="text-lg text-muted-foreground mb-4">
                <strong>
                  Physiotherapist, Mind Motion Physiotherapy & Rehab Center
                </strong>
              </p>

              <p className="italic text-muted-foreground mb-6">
                Helping individuals reduce pain, restore movement, and improve their quality of life through evidence-based rehabilitation.
              </p>

              <p className="text-muted-foreground mb-6 leading-relaxed">
                I am Dr. Gopika KP, a physiotherapist dedicated to helping individuals recover better, move freely, and regain confidence in their daily lives.
              </p>

              <h3 className="text-xl font-bold text-primary mb-2">
                Pain Management
              </h3>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Focused on assessing and treating acute and chronic pain using manual therapy, exercise, and education for long-term relief.
              </p>

              <h3 className="text-xl font-bold text-primary mb-2">
                Neurological Rehabilitation
              </h3>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Rehabilitation for neurological conditions including stroke, nerve injuries, and balance disorders to improve independence and function.
              </p>

              <h3 className="text-xl font-bold text-primary mb-2">
                Musculoskeletal Rehabilitation
              </h3>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Treatment for spine and joint conditions such as neck, back, shoulder, knee, and ankle issues along with sports-specific injuries through structured rehabilitation.
              </p>

              <h3 className="text-xl font-bold text-primary mb-2">
                Women’s Health
              </h3>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Care for women’s health concerns including postnatal recovery, pelvic health, PCOS, and posture-related issues—supporting women at every stage of life.
              </p>

              <h3 className="text-xl font-bold text-primary mb-2">
                Mind Motion Physiotherapy & Rehab Center
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                Delivering personalized physiotherapy with a compassionate and patient-centered approach focused on long-term wellness and recovery.
              </p>
            </div>

            {/* IMAGE */}
            <div className="relative">
              <img
                src={drGopika.image}
                alt="Dr. Gopika KP"
                className="rounded-lg shadow-xl w-full h-[700px] object-cover"
              />
            </div>

          </div>

        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 md:py-24 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-12">
            Our Core Values
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Award, title: "Excellence" },
              { icon: Heart, title: "Compassion" },
              { icon: Target, title: "Results" }
            ].map((item, i) => (
              <Card key={i}>
                <CardContent className="p-8 text-center">
                  <item.icon className="w-8 h-8 mx-auto mb-4 text-primary" />
                  <h3 className="text-xl font-bold">{item.title}</h3>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-primary text-white text-center">
        <h2 className="text-3xl font-bold mb-6">
          Experience Personalized Care
        </h2>

        <Button onClick={handleWhatsApp} size="lg">
          Book Appointment
        </Button>
      </section>
    </div>
  );
};

export default AboutPage;